import { useEffect, useContext, useState } from "react";
import { locationSelectStyle } from "../../utils/functions";
import Button from "../UI/Button/button";
import { ALL_SKILLS } from "../../constants";
import styles from "./searchmodal.module.css";
import Input from "../UI/FormInput/input";
import { IconFilter, IconRotate, IconX } from "@tabler/icons-react";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import ChecklistOption from "../../pages/ClientDashboard/AddNewJob/ChecklistOption";
import { UserContext } from "../../context";
import SkillsMultiSelect from "./SkillsMultiSelect";

export const JOB_TYPES = ["Full time", "Part time", "Internship"];
export const WORK_MODES = ["Onsite", "Work Remotely", "Open to relocate"];

const SearchModal = ({
  setModalOpen,
  setFilters,
  filters,
  handleSearchFilters,
  committedFilters,
  open,
  resultCount,
}) => {
  const { GOOGLE_API_KEY } = useContext(UserContext);
  const [locationValue, setLocationValue] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevState) => ({
      ...prevState,
      [name]: value.replace(/^0+/, ""),
    }));
  };
  const handleResetFilters = () => {
    setFilters({
      role: "",
      skills: "",
      location: "",
      paymentMin: "",
      paymentMax: "",
      jobTypes: null,
      workModes: null,
    });
    setLocationValue(null);
    handleSearchFilters();
  };

  const onClose = () => {
    setModalOpen(false);
  };

  // Sync the draft from the committed filters every time the drawer opens.
  useEffect(() => {
    if (open) setFilters(committedFilters);
  }, [open, committedFilters]);

  if (!open) return null;

  const handleApply = () => {
    handleSearchFilters();
    setModalOpen(false);
  };

  const toggleJobType = (type) => {
    setFilters((prev) => ({
      ...prev,
      jobTypes: prev.jobTypes === type ? "" : type,
    }));
  };

  const toggleMode = (mode) => {
    setFilters((prev) => ({
      ...prev,
      workModes: prev.workModes === mode ? "" : mode,
    }));
  };

  return (
    <div>
      <>
        <div className={styles.overlay} onClick={onClose} />
        <aside className={styles.drawer} aria-label="Advanced search filters">
          <div className={styles.fdHeader}>
            <div>
              <h2>Advanced Search</h2>
              <p>Filter the talent feed to find your match.</p>
            </div>
            <button
              className={styles.fdClose}
              onClick={onClose}
              aria-label="Close filters"
            >
              <IconX size={18} />
            </button>
          </div>

          <div className={styles.fdBody}>
            <div className={styles.filterGroup}>
              <Input
                label="Role"
                id="fRole"
                type="text"
                placeholder="e.g. Software Engineer"
                name="role"
                value={filters?.role}
                onChange={(e) => handleChange(e)}
              />
            </div>

            <div className={styles.filterGroup}>
              <label
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "var(--navy)",
                  display: "block",
                  marginBottom: 7,
                }}
              >
                Location
              </label>

              <GooglePlacesAutocomplete
                apiKey={GOOGLE_API_KEY}
                selectProps={{
                  styles: locationSelectStyle,
                  name: "currentLocation",
                  placeholder: "Type in your location...",
                  value: locationValue,
                  onChange: (e) => {
                    setLocationValue(e);
                    setFilters((prevState) => ({
                      ...prevState,
                      location: e.label,
                    }));
                  },
                  isSearchable: true,
                  onKeyDown: (e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  },
                }}
              />
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Skills</label>
              <SkillsMultiSelect
                allSkills={ALL_SKILLS}
                selected={filters.skills}
                onChange={(skills) =>
                  setFilters((prev) => ({
                    ...prev,
                    skills,
                  }))
                }
              />
            </div>

            <div className={styles.filterDivider} />

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Payment Range (₦)</label>
              <div className={styles.payRow}>
                <Input
                  label="Min"
                  id="fMin"
                  type="number"
                  min="0"
                  placeholder="0"
                  name="paymentMin"
                  value={filters.paymentMin ?? ""}
                  onChange={(e) => handleInputChange(e)}
                />
                <Input
                  label="Max"
                  id="fMax"
                  type="number"
                  min="0"
                  placeholder="Any"
                  name="paymentMax"
                  value={filters.paymentMax ?? ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className={styles.filterDivider} />

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Job Type</label>
              <div className={styles.checkStack}>
                <ChecklistOption
                  label="Show all"
                  checked={filters.jobTypes === ""}
                  onToggle={() =>
                    setFilters((prev) => ({
                      ...prev,
                      jobTypes: "",
                    }))
                  }
                />
                {JOB_TYPES.map((type) => (
                  <ChecklistOption
                    key={type}
                    label={type}
                    checked={filters.jobTypes === type}
                    onToggle={() => toggleJobType(type)}
                  />
                ))}
              </div>
            </div>

            <div className={styles.filterDivider} />

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Work Mode</label>
              <div className={styles.checkStack}>
                {WORK_MODES.map((mode) => (
                  <ChecklistOption
                    key={mode}
                    label={mode}
                    checked={filters.workModes === mode}
                    onToggle={() => toggleMode(mode)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className={styles.fdFooter}>
            <Button
              variant="ghost"
              leftIcon={<IconRotate size={15} />}
              onClick={handleResetFilters}
            >
              Reset
            </Button>
            <Button
              variant="secondary"
              leftIcon={<IconFilter size={15} />}
              onClick={handleApply}
              style={{ flex: 1 }}
            >
              Show {resultCount} result{resultCount === 1 ? "" : "s"}
            </Button>
          </div>
        </aside>
      </>
    </div>
  );
};

export default SearchModal;
